import React, { useCallback } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import GenericMasonry from "src/components/GenericMasonry";
import { searchImagesQuery } from "src/queries/searchImages.graphql";
import useUploadImages from "./hooks/useUploadImages";
import { deleteImageMutation } from "./mutations/images.graphql";

const ImageLibrary = () => {
  const [searchImagesLazy] = useLazyQuery(searchImagesQuery);

  const loadMoreImages = useCallback(
    (offset, limit, searchTerm) =>
      searchImagesLazy({
        variables: {
          input: { limit, offset, searchTerm, includeTotalCount: false },
        },
      }),
    [searchImagesLazy]
  );

  const { uploadImages } = useUploadImages();
  const [deleteImage] = useMutation(deleteImageMutation);

  const normalizeItem = useCallback((item) => {
    return {
      id: item.id,
      src: item.url,
      alt: item.name,
      width: item.width,
      height: item.height,
    };
  }, []);

  const onUploadImages = useCallback(
    async (filesToUpload) => {
      const uploadedImages = await uploadImages(filesToUpload);

      return uploadedImages.map(normalizeItem);
    },
    [uploadImages, normalizeItem]
  );

  const onDeleteImage = useCallback(
    async (imageId) => deleteImage({ variables: { input: { imageId } } }),
    [deleteImage]
  );

  const fetchItems = useCallback(
    async (page, limit) => {
      const { data, error } = await loadMoreImages(page * limit, limit);

      const images = data?.searchImages?.images || [];

      return {
        data: images.map(normalizeItem),
        error,
        hasMore: !!images?.length,
      };
    },
    [loadMoreImages, normalizeItem]
  );

  return (
    <GenericMasonry
      fetchItems={fetchItems}
      onUpload={onUploadImages}
      onDeleteImage={onDeleteImage}
      title="Uploaded images"
    />
  );
};

export default ImageLibrary;
